import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerView } from '../../services/service-evaluation-answer';
import { EvaluationQuestionType } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import { isAnswerable } from './answerDraft';
import styles from './page-evaluation-answer.module.scss';

type SubmittedProps = {
  view: AnswerView;
  canGoToAccurator: boolean;
  backPath: string | null;
};

type StoredAnswer = NonNullable<AnswerView['response']>['answers'][number];

type OnwardLinkProps = { orgSlug: string; backPath: string | null };

export const OnwardLink: FC<OnwardLinkProps> = ({ orgSlug, backPath }) => {
  const [t] = useTranslation('evaluations');
  return (
    <a className={styles.primaryLink} href={backPath ?? `/${orgSlug}/todo`}>
      {backPath
        ? t('answer.backToRequest', 'Tillbaka till utvärderingen')
        : t('answer.goToAccurator', 'Gå till Accurator')}
    </a>
  );
};

export const SubmittedAnswer: FC<SubmittedProps> = ({ view, canGoToAccurator, backPath }) => {
  const [t] = useTranslation('evaluations');
  const response = view.response;
  if (!response) return null;
  const answersByKey = new Map(response.answers.map(answer => [answer.key, answer]));

  const scoreText = (answer: StoredAnswer | undefined) => {
    if (answer?.skipped) return t('answer.skip', 'Vet ej');
    if (answer?.score != null) return t('answer.index.score', '{{score}} av 5', { score: answer.score });
    return t('answer.notAnswered', 'Inte besvarad');
  };

  const stat = (label: string, value: string, name: string) => (
    <div className={styles.stat}>
      <span className={styles.small}>{label}</span>
      <span className={styles.statValue} data-stat={name}>
        {value}
      </span>
    </div>
  );

  return (
    <div className={styles.outcome}>
      <section className={`${styles.card} ${styles.statusCard}`}>
        <span className={styles.okCircle}>
          <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="m5 12 5 5 9-11" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div className={styles.heading}>
          <h1 className={styles.title}>
            {t('answer.submitted.title', 'Tack, ditt svar är inskickat')}
          </h1>
          <p className={styles.muted}>
            {t('answer.submitted.text', 'Svaret går till {{org}} och kan inte ändras.', {
              org: view.org.name,
            })}
          </p>
        </div>
        <div className={styles.stats}>
          {stat(t('answer.average', 'Ditt snitt'), formatScore(response.score), 'average')}
          {stat(t('answer.submitted.scored', 'Betygsatta'), String(response.scoredCount), 'scored')}
          {stat(t('answer.skip', 'Vet ej'), String(response.skippedCount), 'skipped')}
        </div>
        {canGoToAccurator && <OnwardLink orgSlug={view.org.slug} backPath={backPath} />}
      </section>
      <section className={`${styles.card} ${styles.answersCard}`}>
        <h2 className={styles.asideTitle}>{t('answer.yourAnswers', 'Dina svar')}</h2>
        <dl className={styles.answerList}>
          {view.questions.filter(isAnswerable).map(question => {
            const answer = answersByKey.get(question.key);
            const isText = question.type === EvaluationQuestionType.Text;
            return (
              <div key={question.key} className={styles.answerRow}>
                <dt>{question.text}</dt>
                <dd className={styles.answerValue}>
                  {isText
                    ? answer?.text || t('answer.notAnswered', 'Inte besvarad')
                    : scoreText(answer)}
                </dd>
                {!isText && answer?.comment && (
                  <dd className={styles.answerComment}>{answer.comment}</dd>
                )}
              </div>
            );
          })}
        </dl>
      </section>
    </div>
  );
};

type MessageIcon = 'closed' | 'info';

const MESSAGE_ICON_SHAPES: Record<MessageIcon, ReactNode> = {
  closed: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
};

type MessageProps = {
  title: string;
  text?: string;
  icon?: MessageIcon;
  children?: ReactNode;
};

export const AnswerMessage: FC<MessageProps> = ({ title, text, icon, children }) => (
  <div className={styles.outcome}>
    <section className={`${styles.card} ${styles.statusCard}`}>
      {icon && (
        <span className={styles.neutralCircle}>
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {MESSAGE_ICON_SHAPES[icon]}
          </svg>
        </span>
      )}
      <div className={styles.heading}>
        <h1 className={styles.title}>{title}</h1>
        {text && <p className={styles.muted}>{text}</p>}
      </div>
      {children}
    </section>
  </div>
);
